import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, Tab, Tabs, Typography } from '@mui/material';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import AnalyticsService from '../../services/AnalyticsService';
import { viewerSeesRevenue } from '../../utils/analyticsAccess';
import { loadSelection, saveSelection, selectionQuery } from '../../utils/analyticsRange';
import OverviewTab from './OverviewTab';
import ProjectionsTab from './ProjectionsTab';
import ManageTab from './ManageTab';
import { folderKey } from './AnalyticsToolbar';

const TABS = [
  { value: 'overview', label: 'Overview', path: '/analytics' },
  { value: 'projections', label: 'Projections', path: '/analytics/projections', revenueOnly: true },
  { value: 'manage', label: 'Manage', path: '/analytics/manage' },
];
const HIDDEN_FOLDERS_KEY = 'analytics.hiddenFolders';

// Hidden folders are stored (rather than shown ones) so new folders appear.
function loadHiddenFolders() {
  try {
    return new Set(JSON.parse(localStorage.getItem(HIDDEN_FOLDERS_KEY) || '[]'));
  } catch (_) {
    return new Set();
  }
}

const NO_GENERAL_EXPENSES = { current: 0, previous: 0 };

// Totals for the folders currently shown. RPM only counts views from channels
// that report revenue, as on the server. General expenses, which are not for
// any one channel, always count. Without revenue, only views add up.
function totalsFor(groups, showRevenue, generalExpenses = NO_GENERAL_EXPENSES) {
  const sum = (read) => groups.reduce((total, group) => total + read(group), 0);
  const channels = groups.flatMap((group) => group.channels);
  const shared = {
    channelCount: channels.length,
    needsAttention: channels.filter((channel) => channel.problem).length,
    views: sum((group) => group.views),
  };
  if (!showRevenue) return { ...shared, previous: { views: sum((group) => group.previous.views) } };
  const revenue = sum((group) => group.revenue);
  const revenueNet = sum((group) => group.revenueNet);
  const revenueViews = sum((group) => group.revenueViews);
  const expenses = sum((group) => group.expenses) + generalExpenses.current;
  const previousRevenue = sum((group) => group.previous.revenue);
  const previousRevenueNet = sum((group) => group.previous.revenueNet);
  const previousRevenueViews = sum((group) => group.previous.revenueViews);
  const previousExpenses = sum((group) => group.previous.expenses) + generalExpenses.previous;
  return {
    ...shared,
    revenue,
    revenueNet,
    expenses,
    generalExpenses: generalExpenses.current,
    profit: revenueNet - expenses,
    rpm: revenueViews ? (revenue / revenueViews) * 1000 : null,
    projectedMonth: sum((group) => group.projectedMonth),
    projectedMonthGross: sum((group) => group.projectedMonthGross),
    projectedYear: sum((group) => group.projectedYear),
    projectedYearGross: sum((group) => group.projectedYearGross),
    previous: {
      revenue: previousRevenue,
      revenueNet: previousRevenueNet,
      expenses: previousExpenses,
      profit: previousRevenueNet - previousExpenses,
      views: sum((group) => group.previous.views),
      rpm: previousRevenueViews ? (previousRevenue / previousRevenueViews) * 1000 : null,
    },
  };
}

function AnalyticsPage() {
  const { tab = 'overview' } = useParams();
  const navigate = useNavigate();
  const showRevenue = viewerSeesRevenue();
  const tabs = TABS.filter((item) => showRevenue || !item.revenueOnly);
  const [selection, setSelection] = useState(loadSelection);
  const [basis, setBasis] = useState('28');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hiddenFolders, setHiddenFolders] = useState(loadHiddenFolders);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setSummary(await AnalyticsService.summary(selectionQuery(selection), basis));
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load analytics.');
    } finally {
      setLoading(false);
    }
  }, [selection, basis]);

  useEffect(() => {
    load();
  }, [load]);

  function changeSelection(next) {
    saveSelection(next);
    setSelection(next);
  }

  function changeHiddenFolders(next) {
    setHiddenFolders(next);
    try {
      localStorage.setItem(HIDDEN_FOLDERS_KEY, JSON.stringify([...next]));
    } catch (_) {
      // Not remembering the filter is fine.
    }
  }

  const visibleGroups = useMemo(
    () =>
      (summary?.groups || []).filter(
        (group) => group.channelCount > 0 && !hiddenFolders.has(folderKey(group)),
      ),
    [summary, hiddenFolders],
  );
  const totals = useMemo(
    () => totalsFor(visibleGroups, showRevenue, summary?.generalExpenses),
    [visibleGroups, showRevenue, summary],
  );

  if (!tabs.some((item) => item.value === tab)) return <Navigate to="/analytics" replace />;

  const shared = {
    summary,
    loading,
    selection,
    onSelectionChange: changeSelection,
    visibleGroups,
    totals,
    hiddenFolders,
    onHiddenFoldersChange: changeHiddenFolders,
    reload: load,
    showRevenue,
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'flex-end' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 1,
          mb: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, color: 'white', textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}
          >
            Analytics
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.85)' }}>
            {showRevenue
              ? 'Revenue and audience across your YouTube channels.'
              : 'Views and audience across your YouTube channels.'}
          </Typography>
        </Box>
        <Tabs
          value={tab}
          onChange={(_, value) => navigate(tabs.find((item) => item.value === value).path)}
          textColor="inherit"
          TabIndicatorProps={{ sx: { bgcolor: 'white', height: 3, borderRadius: 2 } }}
          sx={{
            color: 'white',
            minHeight: 40,
            '& .MuiTab-root': { minHeight: 40, fontWeight: 600, opacity: 0.8, px: 2 },
            '& .Mui-selected': { opacity: 1 },
          }}
          variant="scrollable"
          allowScrollButtonsMobile
        >
          {tabs.map((item) => (
            <Tab key={item.value} value={item.value} label={item.label} />
          ))}
        </Tabs>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!summary ? (
        loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: 'white' }} />
          </Box>
        )
      ) : (
        <>
          {tab === 'overview' && <OverviewTab {...shared} />}
          {tab === 'projections' && (
            <ProjectionsTab {...shared} basis={basis} onBasisChange={setBasis} />
          )}
          {tab === 'manage' && <ManageTab {...shared} />}
        </>
      )}
    </Box>
  );
}

export default AnalyticsPage;
